import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { wrapProjects } from '../data/projects'

const Services: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'brand-identity',
    message: '',
  })

  const capabilities = [
    {
      title: 'Brand & Identity',
      description: 'Complete brand systems that define who you are.',
      services: [
        'Logo Design & Refresh',
        'Brand Guidelines',
        'Color Systems & Typography',
        'Brand Strategy',
        'Visual Identity Systems',
      ],
    },
    {
      title: 'Digital & Web',
      description: 'Websites and digital experiences that convert.',
      services: [
        'Website Design & Development',
        'E-Commerce Platforms',
        'Landing Pages',
        'UI/UX Design',
        'Responsive Design',
      ],
    },
    {
      title: 'Vehicle Graphics',
      description: 'Turn your fleet into mobile billboards.',
      services: [
        'Full Vehicle Wraps',
        'Partial Wraps',
        'Fleet Graphics',
        'Design Concepts',
        'Print-Ready Files',
      ],
    },
    {
      title: 'Print & Collateral',
      description: 'Physical touchpoints that leave an impression.',
      services: [
        'Business Cards & Stationery',
        'Brochures & Catalogs',
        'Packaging Design',
        'Signage & Banners',
        'Marketing Materials',
      ],
    },
  ]

  const process = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We learn about your business, goals, and vision.',
    },
    {
      step: '02',
      title: 'Strategy',
      description: 'We develop a creative direction aligned with your objectives.',
    },
    {
      step: '03',
      title: 'Design',
      description: 'We create concepts and refine based on your feedback.',
    },
    {
      step: '04',
      title: 'Delivery',
      description: 'You receive production-ready files and ongoing support.',
    },
  ]

  const featuredWork = wrapProjects
    .filter(p => !p.isTextBox)
    .slice(0, 6)
    .map(project => ({
      name: project.title,
      type: 'Vehicle Graphics',
      image: project.images[0],
      isCaseStudy: false,
    }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Form submitted:', formData)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-background text-primary">
      {/* Hero */}
      <section className="pt-40 pb-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h1
            className="text-6xl md:text-8xl font-display font-medium tracking-tighter text-white mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            Services
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-neutral-400 font-light leading-relaxed max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            A full-service creative studio for brands that refuse to blend in. From strategy to execution, we handle every touchpoint.
          </motion.p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-4 text-white">What we do</h2>
            <p className="text-xl text-neutral-400 font-light">Everything you need to build and grow your brand.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                className="bg-background p-12 md:p-16 hover:bg-surfaceHighlight/30 transition-colors duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <h3 className="text-3xl font-display font-medium mb-4 text-white">{capability.title}</h3>
                <p className="text-neutral-400 font-light mb-8 leading-relaxed text-lg">
                  {capability.description}
                </p>
                <ul className="space-y-3">
                  {capability.services.map((service) => (
                    <li key={service} className="text-neutral-400 font-light flex items-start gap-3 group">
                      <span className="text-accent mt-1 group-hover:translate-x-1 transition-transform">—</span>
                      <span className="group-hover:text-white transition-colors">{service}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-4 text-white">Featured work</h2>
            <p className="text-xl text-neutral-400 font-light">Recent projects across all capabilities.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {featuredWork.map((work, index) => {
              const WrapperComponent = work.isCaseStudy ? Link : 'div';
              const wrapperProps = work.isCaseStudy ? { to: '#' } : {};

              return (
                <WrapperComponent
                  key={work.name}
                  {...wrapperProps}
                  // @ts-ignore
                  className={`group bg-background overflow-hidden ${work.isCaseStudy ? 'cursor-pointer' : ''}`}
                  // @ts-ignore
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  <motion.div>
                    {work.image && (
                      <div className="aspect-[4/3] bg-neutral-900 overflow-hidden relative">
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                        <img
                          src={work.image}
                          alt={work.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                      </div>
                    )}
                    <div className="p-8 border-t border-white/5">
                      <div className="text-xs text-neutral-500 mb-2 font-bold uppercase tracking-wider">{work.type}</div>
                      <h3 className="font-display font-medium text-xl text-white group-hover:text-accent transition-colors">{work.name}</h3>
                    </div>
                  </motion.div>
                </WrapperComponent>
              );
            })}
          </div>

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center text-sm text-neutral-400 hover:text-white transition-colors font-bold uppercase tracking-wider gap-2"
            >
              View all work
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-4 text-white">How we work</h2>
            <p className="text-xl text-neutral-400 font-light">A proven process that delivers results.</p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <div className="text-6xl font-display font-bold text-white/5 mb-6 absolute -top-10 -left-4 select-none">{step.step}</div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-display font-medium mb-4 text-white">{step.title}</h3>
                  <p className="text-neutral-400 font-light leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="quote" className="py-32 px-6 border-t border-white/5 bg-surfaceHighlight/20">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-6 text-white">Let's work together</h2>
            <p className="text-xl text-neutral-400 font-light">Tell us about your project. We'll respond within 24 hours.</p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-8 bg-background p-8 md:p-12 border border-white/5 shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white placeholder-neutral-700 text-lg"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Project Type</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors bg-transparent font-light text-white text-lg appearance-none"
                >
                  <option value="brand-identity" className="bg-neutral-900">Brand & Identity</option>
                  <option value="web-design" className="bg-neutral-900">Digital & Web</option>
                  <option value="vehicle-graphics" className="bg-neutral-900">Vehicle Graphics</option>
                  <option value="print-collateral" className="bg-neutral-900">Print & Collateral</option>
                  <option value="full-service" className="bg-neutral-900">Full Service Project</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Tell us about your project goals, timeline, and budget..."
                className="w-full px-0 py-3 border-0 border-b border-neutral-800 focus:border-accent focus:ring-0 transition-colors resize-none bg-transparent font-light text-white placeholder-neutral-700 text-lg"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="w-full px-8 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
              >
                Send inquiry
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  )
}

export default Services
